import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import type { Person } from '../../../users/entities/person.js';

const dialogContent = stateful(
  (props: {
    user: NarrowedEntity<Person, { _id: 1; microsoftUserData: { manualMails: 1 } }>;
    reject: (value: null) => void;
    resolve: (value: string[]) => void;
  }) => {
    const [values, setValues] = useState(props.user.microsoftUserData?.manualMails ?? []);
    const [newValue, setNewValue] = useState('');

    const addAddress = (alias: Maybe<string>) => {
      if (!alias) return;
      setValues([...values, alias]);
      setNewValue('');
    };

    return html`
      <div class="dialog-container max-w-xl">
        ${DialogHeader({ title: 'Zusätzliche E-Mail-Adressen', close: () => props.reject(null), closeLabel: 'Dialog schließen' })}

        <p class="text-sm text-base-content/70">
          Die Adressen aus den Schulen einer Person werden automatisch aus ihren Rollen erzeugt und stehen nicht in
          dieser Liste. Hier stehen nur zusätzlich von Hand eingetragene Adressen — inklusive Domain.
        </p>

        <p class="flex gap-2 rounded-box border border-warning/40 bg-warning/5 p-2 text-sm text-base-content/80">
          <span class="icon-[material-symbols--info-outline-rounded] shrink-0 text-lg text-warning"></span>
          <span>
            Diese Adressen werden im Portal festgehalten, aber <b>nicht automatisch bei Microsoft zugestellt</b>. Ob
            eine Adresse wirklich ankommt, entscheidet Exchange über die Proxy-Adressen des Postfachs — sichtbar im
            Reiter unter „Zustellbare Adressen“. Zum Zustellen muss die Adresse im Exchange Admin Center am Postfach
            ergänzt werden.
          </span>
        </p>

        <ul class="flex flex-col gap-2">
          ${
            values.length
              ? repeat(
                  values,
                  mail => mail,
                  (mail, index) => html`
                    <li class="flex items-center justify-between gap-2 rounded-box border border-base-300 p-2">
                      <span class="min-w-0 truncate text-sm">${mail}</span>
                      <button
                        class="btn btn-ghost btn-sm btn-square text-error"
                        title="Adresse entfernen"
                        @click=${() => setValues(values.filter((_, position) => position !== index))}
                      >
                        <span class="icon-[material-symbols--delete-outline]"></span>
                      </button>
                    </li>
                  `,
                )
              : html`<li class="text-sm text-base-content/70">Keine zusätzlichen Adressen</li>`
          }
        </ul>

        <div class="flex items-center gap-2 rounded-box border border-base-300 p-2">
          <div class="grow">
            ${TextInput({
              value: newValue,
              type: 'email',
              placeholder: 'E-Mail-Adresse',
              onChanged: value => setNewValue(value ?? ''),
              onPicked: value => setNewValue(value ?? ''),
              onSubmit: value => addAddress(value),
            })}
          </div>
          <button
            class="btn btn-ghost btn-sm btn-square text-success"
            title="Adresse hinzufügen"
            @click=${() => addAddress(newValue)}
          >
            <span class="icon-[material-symbols--add]"></span>
          </button>
        </div>

        <button
          class="btn btn-primary"
          @click=${() => {
            // maybe the user forgot to add a new value via the button or enter. In this case we just take the fields value and push it if not empty
            props.resolve(newValue ? [...values, newValue] : values);
          }}
        >
          <span class="icon-[material-symbols--save]"></span>
          Speichern
        </button>
      </div>
    `;
  },
);

export const SgMicrosoftEmailAliasEditDialog = stateful(
  (props: { user: NarrowedEntity<Person, { _id: 1; microsoftUserData: { manualMails: 1 } }> }) => html`
    <button
      class="btn btn-ghost btn-sm btn-square shrink-0"
      title="Zusätzliche E-Mail-Adressen bearbeiten"
      @click=${async () => {
        if (!props.user.microsoftUserData) return;
        let newValues: string[] | null = null;
        try {
          newValues = await showDialog<string[] | null>({
            content: (resolve, reject) => dialogContent({ user: props.user, reject, resolve }),
          });
        } catch {
          return;
        }

        if (!newValues) return;

        if (
          !(await confirmDialog(
            'Zusätzliche Adressen wirklich ändern? Sie werden im Portal gespeichert — die Zustellung muss in Exchange eingerichtet werden.',
          ))
        )
          return;

        // ! manualMails, not otherMails. ! `otherMails` is computed from the person's roles on every
        // sync, so anything typed into it survived exactly until their next save.
        props.user.microsoftUserData.manualMails = newValues;
        await guardDialog(() => props.user.save());
      }}
    >
      <span class="icon-[material-symbols--edit]"></span>
    </button>
  `,
);
